// ────────────────────────────────────────────────────────────────────────
// WeddingSetupModal — gateway modal collected BEFORE the tailor can access
// the Wedding tab. Captures the couple's identity (groom, bride, date).
// Visual language mirrors the existing in-tab "Plan Wedding Party" CTA
// card so the gateway and the planner feel like one cohesive ritual.
//
// (Iter 78) — palette section removed per user spec; inline calendar
// replaces the native <input type="date"> + an in-modal "Plan the Wedding
// Section" CTA fires the WeddingPartyQuestionnaire flow in one click once
// the couple identity is saved.
// ────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Calendar } from './ui/calendar';
import { Loader2, Heart, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

export default function WeddingSetupModal({
  open, onClose, onSubmit,
  initialGroomName = '',
  initialBrideName = '',
  initialWeddingDate = '',
  saving = false,
}) {
  const [groomName, setGroomName] = useState(initialGroomName || '');
  const [brideName, setBrideName] = useState(initialBrideName || '');
  // Internal date state is a JS Date for the Calendar widget — we
  // serialise to YYYY-MM-DD only on submit.
  const [weddingDate, setWeddingDate] = useState(initialWeddingDate ? new Date(initialWeddingDate + 'T00:00:00') : null);

  // Re-hydrate every time the modal opens so editing a previously-saved
  // client never carries stale state from a different client.
  useEffect(() => {
    if (open) {
      setGroomName(initialGroomName || '');
      setBrideName(initialBrideName || '');
      setWeddingDate(initialWeddingDate ? new Date(initialWeddingDate + 'T00:00:00') : null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // YYYY-MM-DD serializer that respects local time (avoids UTC shift edge
  // case where Sept 12 in the picker becomes Sept 11 in ISO string).
  const toIsoDate = (d) => {
    if (!d) return '';
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const isValid = !!(groomName.trim() && brideName.trim() && weddingDate);

  const handleSave = () => {
    if (!isValid) {
      toast.error('Groom name, bride name and wedding date are required');
      return;
    }
    onSubmit?.({
      groom_name: groomName.trim(),
      bride_name: brideName.trim(),
      wedding_date: toIsoDate(weddingDate),
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose?.()}>
      <DialogContent
        className="max-w-2xl bg-[#0A0A0C] border border-[#27272A] rounded-none p-0 max-h-[90vh] overflow-y-auto"
        data-testid="wedding-setup-modal"
      >
        <DialogHeader className="px-6 py-5 border-b border-[#27272A] sticky top-0 bg-[#0A0A0C] z-10">
          <DialogTitle className="text-2xl font-light text-[#F5F5F5] flex items-center gap-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            <Heart className="w-5 h-5" style={{ color: 'hsl(var(--accent))' }} />
            Wedding Setup
          </DialogTitle>
          <DialogDescription className="text-xs text-[#A1A1AA] mt-1">
            Couple identity + wedding date · captured once, used on every wedding render and the final lookbook PDF cover.
          </DialogDescription>
        </DialogHeader>

        <div className="p-6 space-y-6">
          {/* Couple identity */}
          <section>
            <Label className="label-luxury mb-2 block">The Couple</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="block text-[10px] text-[#A1A1AA] uppercase tracking-wider mb-1">Groom Name *</Label>
                <Input
                  value={groomName}
                  onChange={(e) => setGroomName(e.target.value)}
                  placeholder="e.g. James Whitmore"
                  className="bg-[#121215] border-[#27272A] text-[#F5F5F5] rounded-none"
                  data-testid="wedding-setup-groom-name"
                />
              </div>
              <div>
                <Label className="block text-[10px] text-[#A1A1AA] uppercase tracking-wider mb-1">Bride Name *</Label>
                <Input
                  value={brideName}
                  onChange={(e) => setBrideName(e.target.value)}
                  placeholder="e.g. Olivia Hart"
                  className="bg-[#121215] border-[#27272A] text-[#F5F5F5] rounded-none"
                  data-testid="wedding-setup-bride-name"
                />
              </div>
            </div>
          </section>

          {/* Wedding date — large inline calendar */}
          <section className="pt-2 border-t border-[#27272A]">
            <Label className="label-luxury mb-3 block">Wedding Date *</Label>
            <div className="flex flex-col items-center" data-testid="wedding-setup-calendar-wrap">
              <Calendar
                mode="single"
                selected={weddingDate}
                onSelect={setWeddingDate}
                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                fromYear={new Date().getFullYear()}
                toYear={new Date().getFullYear() + 5}
                showOutsideDays
                className="bg-[#121215] border border-[#27272A] p-4 text-[#F5F5F5] [--accent:theme(colors.amber.300)] [&_.rdp-day_selected]:!bg-[hsl(var(--accent))] [&_.rdp-day_selected]:!text-[#0A0A0C] [&_.rdp-day:hover:not([disabled])]:bg-[#1A1A1E] [&_.rdp-day_today]:!text-[hsl(var(--accent))] [&_.rdp-nav_button]:text-[#A1A1AA] [&_.rdp-nav_button:hover]:!bg-[#1A1A1E] [&_.rdp-caption_label]:!text-[#F5F5F5] [&_.rdp-head_cell]:!text-[#52525B]"
                data-testid="wedding-setup-calendar"
              />
              {weddingDate ? (
                <p className="mt-3 text-xs text-[#A1A1AA] uppercase tracking-wider">
                  Selected · <span className="text-[hsl(var(--accent))]">{weddingDate.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                </p>
              ) : (
                <p className="mt-3 text-[10px] text-[#52525B] italic">Tap a date to lock in the ceremony</p>
              )}
            </div>
          </section>

          {/* Save footer — single primary action returns to the wedding
              dashboard (the in-tab "Plan Wedding Party" CTA already lives
              there, so we don't duplicate it inside the modal). */}
          <div className="pt-4 border-t border-[#27272A] flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-end">
            <Button
              variant="ghost"
              onClick={onClose}
              disabled={saving}
              className="text-[#A1A1AA] hover:bg-[#1A1A1E] rounded-none"
              data-testid="wedding-setup-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!isValid || saving}
              className="rounded-none uppercase tracking-wider text-xs px-6 py-5"
              style={{ backgroundColor: 'hsl(var(--accent))', color: '#0A0A0C' }}
              data-testid="wedding-setup-save"
            >
              {saving
                ? (<><Loader2 className="w-4 h-4 spinner mr-2" /> Saving…</>)
                : (<>Go to Wedding Dashboard <ArrowRight className="w-4 h-4 ml-2" /></>)}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
